import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import type { IPublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { authFetch, authGetSwr, readPersistedJson } from "../utils/authFetch";
import { applyLicensedModules, applyPlatformModuleMeta, type PlatformModuleMeta } from "../data/securityModules";
import { readLicensedModules } from "../utils/modulePlanConfig";

const PLATFORM_MODULES_URL = "/api/entitlements?platformModules=1";

function readPlatformMetaResponse(data: unknown): Record<string, PlatformModuleMeta> | null {
  if (!data || typeof data !== "object") return null;
  const meta = (data as { meta?: unknown }).meta;
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return null;
  return meta as Record<string, PlatformModuleMeta>;
}

// Pakete wie im Produktkatalog 2026. `starter` und `enterprise` sind die alten
// Namen von `essential` und `complete` und bleiben lesbar, bis der Mandant im
// GA-Portal einmal gespeichert wurde.
export type TenantPlan = "trial" | "essential" | "professional" | "complete" | "starter" | "enterprise";
export type TenantStatus = "active" | "trial" | "suspended" | "inactive";
// Plan-ID: einer der Standard-Pläne oder ein Tenant-eigener Plan aus
// features.training_plans (siehe utils/trainingPlans.ts).
export type DefaultLevel = string;
export const DEFAULT_MODULE_VALIDITY_MONTHS = 12;
// "year_end": gültig bis 31.12. des Folgejahres (Standard); "months": feste Monate ab Abschluss.
export type ModuleValidityMode = "year_end" | "months";

export type TenantFeatures = {
  phishing_campaigns: boolean;
  advanced_reports: boolean;
  training_modules_max: number;
  seats_max: number;
  notifications: boolean;
  export_csv: boolean;
  default_level: DefaultLevel;
  module_validity_months: number;
  module_validity_mode: ModuleValidityMode;
  failure_escalation: boolean;
  test_enabled: boolean;
  /**
   * Eigener Modulbaukasten (`/admin/eigene-module`). Gehört zum höchsten Paket
   * und ist deshalb wie `licensed_modules` nur im GA-Portal setzbar –
   * `entitlements.js` verwirft das Feld beim PUT.
   */
  custom_modules: boolean;
  /** Content-Admins: dürfen nur Module bearbeiten. Gepflegt über contentAdminsPatch (entitlements.js). */
  content_admins?: string[];
  /** Globale Standard-Bestehensgrenze in Prozent für eigene Module (0-100). Fehlt = 80. */
  pass_threshold?: number;
  group_modules?: Record<string, string[]>;
  module_plan_overrides?: Record<string, string>;
  active_modules?: string[];
  /**
   * Im GA-Portal freigeschaltete Module. Nur was hier steht, existiert für den
   * Mandanten überhaupt (siehe applyLicensedModules).
   * Bewusst getrennt von `active_modules`: das ist das Kontingent, das der
   * Tenant-Admin selbst setzt — Lizenzen darf er nicht selbst vergeben.
   */
  licensed_modules?: string[];
  /**
   * Für den Mandanten freigeschaltete Oberflächensprachen. Fehlt das Feld,
   * gelten beide (`de` + `en`) — so verhalten sich Bestandsmandanten wie bisher.
   * Wie `licensed_modules` nur im GA-Portal setzbar: `entitlements.js` verwirft
   * das Feld beim PUT, sonst könnte ein Tenant-Admin sich Sprachen selbst geben.
   */
  languages?: string[];
  module_folders?: unknown; // Themen mit Tenant-Farbe, siehe utils/moduleTopics.ts
  module_contacts?: unknown; // Ansprechpartner pro Modul, siehe utils/moduleContacts.ts
  topic_contacts?: unknown; // Experte pro Thema (Fallback für Module ohne eigenen Ansprechpartner)
  training_plans?: unknown; // Umbenennbare Plan-Liste (max. 6), siehe utils/trainingPlans.ts
  cobrand_logo?: string; // Kunden-Logo (Light Mode) als Data-URL, neben dem AWARE-Logo gezeigt
  cobrand_logo_dark?: string; // Kunden-Logo-Variante für den Dark Mode (Fallback: cobrand_logo)
  cobrand_name?: string; // Anzeigename/Alt-Text fürs Kunden-Logo
  userProfiles?: Record<string, unknown>;
  phishing_config?: unknown;
  escalation_policy?: unknown;
  status_mails?: unknown; // Monatliches Statusupdate, siehe utils/statusMails.ts
  phishingAutoCampaigns?: unknown;
  entra_purge?: unknown; // Löschung von in Entra gelöschten Konten, siehe utils/entraPurge.ts
  inactive_retention?: unknown; // Löschung nach Inaktivität, siehe utils/entraPurge.ts → normalizeInactiveRetention
  entra_missing?: unknown; // Karenz-Zähler, wird ausschließlich vom Cron gepflegt
};

export type TenantEntitlement = {
  tenantId: string;
  name: string;
  plan: TenantPlan;
  status: TenantStatus;
  features: TenantFeatures;
  validUntil: string;
  createdAt: string;
  notes?: string;
  admins?: string[];
  /** Das eigene Konto wurde vom Admin deaktiviert – die App zeigt nur den Sperrhinweis. */
  accountDeactivated?: boolean;
};

// null = Logo entfernen, undefined = unverändert lassen.
export type CobrandPatch = {
  logo?: string | null;
  logoDark?: string | null;
  name?: string;
};

type EntitlementContextValue = {
  entitlement: TenantEntitlement | null;
  hasFeature: (feature: keyof TenantFeatures) => boolean;
  isFeatureLimited: (feature: "training_modules_max" | "seats_max", count: number) => boolean;
  saveEntitlement: (e: TenantEntitlement) => Promise<boolean>;
  patchCobrand: (patch: CobrandPatch) => Promise<boolean>;
  /**
   * Bereits gespeicherte Feature-Werte lokal übernehmen, ohne neuen Request.
   * Pflicht nach jedem gezielten Feature-Patch: Seiten wie Modul-Editor und
   * Modul-Pläne lesen denselben Stand, und ohne das zeigt die andere Seite
   * bis zum Neuladen den alten Wert – und schreibt ihn beim Speichern zurück.
   */
  applyFeatures: (patch: Partial<TenantFeatures>) => void;
  /** Mandanten-Stand frisch vom Server holen (am persistenten Cache vorbei). */
  refreshEntitlement: () => void;
  isLoading: boolean;
  isLocked: boolean;
  expiryBusinessDays: number | null;
  /** Zählt hoch, sobald globale Modul-Overrides eingespielt wurden. */
  moduleMetaVersion: number;
};

// Vorgabe je Paket. Nur was der Mandant selbst nicht gespeichert hat, kommt von
// hier — die tatsächlichen Werte stehen in tenant_entitlements.features und
// werden im GA-Portal gesetzt (siehe gaShared.tsx → PLAN_DEFAULTS, gleiche Tabelle).
const PLAN_FEATURE_DEFAULTS: Record<TenantPlan, Pick<TenantFeatures,
  "phishing_campaigns" | "advanced_reports" | "seats_max" | "notifications" | "export_csv" | "failure_escalation" | "custom_modules">> = {
  trial: { phishing_campaigns: true, advanced_reports: true, seats_max: 10, notifications: false, export_csv: true, failure_escalation: false, custom_modules: false },
  essential: { phishing_campaigns: false, advanced_reports: false, seats_max: 25, notifications: false, export_csv: true, failure_escalation: false, custom_modules: false },
  professional: { phishing_campaigns: true, advanced_reports: true, seats_max: 100, notifications: true, export_csv: true, failure_escalation: true, custom_modules: false },
  complete: { phishing_campaigns: true, advanced_reports: true, seats_max: -1, notifications: true, export_csv: true, failure_escalation: true, custom_modules: true },
  // Alte Paketnamen derselben Stufen.
  starter: { phishing_campaigns: false, advanced_reports: false, seats_max: 25, notifications: false, export_csv: true, failure_escalation: false, custom_modules: false },
  enterprise: { phishing_campaigns: true, advanced_reports: true, seats_max: -1, notifications: true, export_csv: true, failure_escalation: true, custom_modules: true },
};

export function getDefaultFeatures(plan: TenantPlan): TenantFeatures {
  return {
    ...PLAN_FEATURE_DEFAULTS[plan],
    training_modules_max: -1,
    default_level: "standard",
    module_validity_months: DEFAULT_MODULE_VALIDITY_MONTHS,
    module_validity_mode: "year_end",
    test_enabled: true,
  };
}

const VALID_PLANS: TenantPlan[] = ["trial", "essential", "professional", "complete", "starter", "enterprise"];
const VALID_STATUSES: TenantStatus[] = ["active", "trial", "suspended", "inactive"];

function parseFeatureNumber(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  const rounded = Math.round(value);
  if (rounded === -1 || rounded >= 0) return rounded;
  return fallback;
}

function normalizeDateValue(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

function parseExpiryTimestamp(validUntil: string): number | null {
  const normalized = validUntil.trim();
  if (!normalized) return null;

  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  const parsed = Date.parse(
    dateOnlyPattern.test(normalized)
      ? `${normalized}T23:59:59.999Z`
      : normalized
  );
  return Number.isFinite(parsed) ? parsed : null;
}

function canUseEntitlement(entitlement: TenantEntitlement | null): entitlement is TenantEntitlement {
  if (!entitlement) return false;
  if (entitlement.status !== "active" && entitlement.status !== "trial") return false;

  const expiryTimestamp = parseExpiryTimestamp(entitlement.validUntil);
  if (expiryTimestamp === null) return false;
  return Date.now() <= expiryTimestamp;
}

// Voll-App-Sperre: bei suspended/inactive ODER abgelaufenem Datum.
// Leeres/ungültiges Datum sperrt NICHT (Sicherheit gegen versehentliche Aussperrung).
// Kein geladenes Entitlement (noch im Ladevorgang oder Fetch-Fehler) sperrt NICHT.
function isTenantLocked(entitlement: TenantEntitlement | null): boolean {
  if (!entitlement) return false;
  if (entitlement.status === "suspended" || entitlement.status === "inactive") return true;
  const expiryTimestamp = parseExpiryTimestamp(entitlement.validUntil);
  if (expiryTimestamp === null) return false;
  return Date.now() > expiryTimestamp;
}

// Verbleibende Werktage (Mo–Fr) bis einschließlich Ablaufdatum. null = kein gültiges Datum.
function businessDaysUntil(validUntil: string): number | null {
  const expiryTimestamp = parseExpiryTimestamp(validUntil);
  if (expiryTimestamp === null) return null;
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(expiryTimestamp);
  if (end.getTime() < cursor.getTime()) return 0;
  let count = 0;
  for (let i = 0; i < 1000 && cursor.getTime() <= end.getTime(); i++) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

export function normalizeEntitlement(raw: unknown, fallbackTenantId: string): TenantEntitlement | null {
  if (!raw || typeof raw !== "object") return null;

  const candidate = raw as Record<string, unknown>;
  // Deaktiviertes Konto: Der Server liefert dann bewusst keine Mandantendaten.
  if (candidate.accountDeactivated === true) {
    return { ...createTrialEntitlement(fallbackTenantId), accountDeactivated: true };
  }
  const plan = VALID_PLANS.includes(candidate.plan as TenantPlan)
    ? (candidate.plan as TenantPlan)
    : "trial";
  const status = VALID_STATUSES.includes(candidate.status as TenantStatus)
    ? (candidate.status as TenantStatus)
    : "trial";

  const defaultFeatures = getDefaultFeatures(plan);
  const rawFeatures = candidate.features;
  const featureSource = rawFeatures && typeof rawFeatures === "object"
    ? (rawFeatures as Record<string, unknown>)
    : null;

  const features: TenantFeatures = {
    ...(featureSource ?? {}),
    phishing_campaigns: typeof featureSource?.phishing_campaigns === "boolean"
      ? featureSource.phishing_campaigns
      : defaultFeatures.phishing_campaigns,
    advanced_reports: typeof featureSource?.advanced_reports === "boolean"
      ? featureSource.advanced_reports
      : defaultFeatures.advanced_reports,
    training_modules_max: parseFeatureNumber(featureSource?.training_modules_max, defaultFeatures.training_modules_max),
    seats_max: parseFeatureNumber(featureSource?.seats_max, defaultFeatures.seats_max),
    // Automatische Mails gibt es erst ab Professional. Das Paket ist die
    // Obergrenze: steht bei einem Essential- oder Test-Mandanten noch ein `true`
    // von früher in den Features, gilt es trotzdem nicht. Serverseitig prüft
    // `notificationsLicensed()` in `api/_modulePlan.js` dieselbe Regel.
    notifications: PLAN_FEATURE_DEFAULTS[plan].notifications
      && (typeof featureSource?.notifications === "boolean" ? featureSource.notifications : true),
    export_csv: typeof featureSource?.export_csv === "boolean"
      ? featureSource.export_csv
      : defaultFeatures.export_csv,
    default_level: typeof featureSource?.default_level === "string" && featureSource.default_level.trim()
      ? featureSource.default_level.trim().slice(0, 60)
      : defaultFeatures.default_level,
    module_validity_months: (() => {
      const raw = featureSource?.module_validity_months;
      if (typeof raw === "number" && Number.isFinite(raw) && raw > 0 && raw <= 120) {
        return Math.round(raw);
      }
      return defaultFeatures.module_validity_months;
    })(),
    module_validity_mode: featureSource?.module_validity_mode === "months" ? "months" : "year_end",
    failure_escalation: typeof featureSource?.failure_escalation === "boolean"
      ? featureSource.failure_escalation
      : defaultFeatures.failure_escalation,
    custom_modules: typeof featureSource?.custom_modules === "boolean"
      ? featureSource.custom_modules
      : defaultFeatures.custom_modules,
    test_enabled: typeof featureSource?.test_enabled === "boolean"
      ? featureSource.test_enabled
      : defaultFeatures.test_enabled,
    pass_threshold: (() => {
      const raw = featureSource?.pass_threshold;
      if (typeof raw === "number" && Number.isFinite(raw) && raw > 0 && raw <= 100) return Math.round(raw);
      return undefined;
    })(),
  };

  const tenantIdCandidate = typeof candidate.tenantId === "string" ? candidate.tenantId.trim() : "";
  const nameCandidate = typeof candidate.name === "string" ? candidate.name.trim() : "";
  const tenantId = tenantIdCandidate || fallbackTenantId;
  if (!tenantId) return null;

  const rawAdmins = candidate.admins;
  const admins: string[] | undefined = Array.isArray(rawAdmins)
    ? rawAdmins.filter((e: unknown): e is string => typeof e === "string").map((e) => e.trim().toLowerCase()).filter(Boolean)
    : undefined;

  return {
    tenantId,
    name: nameCandidate || "Unbekannt",
    plan,
    status,
    features,
    validUntil: normalizeDateValue(candidate.validUntil),
    createdAt: normalizeDateValue(candidate.createdAt),
    notes: typeof candidate.notes === "string" ? candidate.notes : undefined,
    admins,
  };
}

function createTrialEntitlement(tenantId: string): TenantEntitlement {
  const now = new Date();
  const validUntil = new Date(now);
  validUntil.setDate(validUntil.getDate() + 30);

  return {
    tenantId,
    name: "Unbekannt",
    plan: "trial",
    status: "trial",
    features: getDefaultFeatures("trial"),
    validUntil: validUntil.toISOString().split("T")[0],
    createdAt: now.toISOString().split("T")[0],
  };
}

async function fetchEntitlementFromApi(tenantId: string): Promise<TenantEntitlement | null> {
  try {
    const res = await authFetch(`/api/entitlements?tenantId=${encodeURIComponent(tenantId)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json() as unknown;
    return normalizeEntitlement(data, tenantId);
  } catch {
    return null;
  }
}

async function fetchOrgDisplayNameFromGraph(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<string | null> {
  try {
    // Bootstrap should never trigger a popup – stay silent or give up.
    const tokenResult = await instance.acquireTokenSilent({
      scopes: ["User.Read"],
      account,
    });
    const r = await fetch("https://graph.microsoft.com/v1.0/organization?$select=displayName", {
      headers: { Authorization: `Bearer ${tokenResult.accessToken}` },
    });
    if (!r.ok) return null;
    const data = await r.json() as { value?: Array<{ displayName?: string }> };
    const name = data.value?.[0]?.displayName?.trim();
    return name || null;
  } catch {
    return null;
  }
}

function isPlaceholderName(name: string): boolean {
  const n = name.trim().toLowerCase();
  return n === "" || n === "unbekannt" || n === "unknown";
}

async function saveEntitlementToApi(e: TenantEntitlement): Promise<boolean> {
  try {
    const res = await authFetch("/api/entitlements", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(e),
    });
    return res.ok;
  } catch {
    return false;
  }
}

const defaultContextValue: EntitlementContextValue = {
  entitlement: null,
  hasFeature: () => false,
  isFeatureLimited: () => true,
  saveEntitlement: async () => false,
  patchCobrand: async () => false,
  applyFeatures: () => undefined,
  refreshEntitlement: () => undefined,
  isLoading: true,
  isLocked: false,
  expiryBusinessDays: null,
  moduleMetaVersion: 0,
};

const EntitlementContext = createContext<EntitlementContextValue>(defaultContextValue);

export function EntitlementProvider({ children, tenantId }: { children: ReactNode; tenantId: string }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  // Lazy-Init aus dem persistenten GET-Cache (siehe authFetch.ts): steht z.B.
  // das Kunden-Logo schon beim ersten Render zur Verfügung, statt erst nach dem
  // async Fetch in die Sidebar zu poppen und die Nav-Items nach unten zu schieben.
  const [entitlement, setEntitlement] = useState<TenantEntitlement | null>(() => {
    if (!tenantId) return null;
    const cached = readPersistedJson(`/api/entitlements?tenantId=${encodeURIComponent(tenantId)}`);
    return normalizeEntitlement(cached, tenantId);
  });
  const [isLoading, setIsLoading] = useState(true);
  // Im GA-Portal gepflegte Modul-Metadaten: einmal pro Session holen und in
  // SECURITY_MODULES einspielen (siehe applyPlatformModuleMeta). Der Zähler
  // erzwingt danach ein Re-Render des gesamten Provider-Teilbaums, weil die
  // Modul-Listen ihre Daten direkt aus dem Modul-Export lesen.
  const [platformMetaVersion, setPlatformMetaVersion] = useState(() => {
    // Lizenzen zuerst: applyPlatformModuleMeta arbeitet auf der sichtbaren Modulliste.
    applyLicensedModules(readLicensedModules(entitlement?.features ?? null));
    const cached = readPlatformMetaResponse(readPersistedJson(PLATFORM_MODULES_URL));
    if (!cached) return 0;
    applyPlatformModuleMeta(cached);
    return 1;
  });

  // Lizenzierte Module in SECURITY_MODULES einspielen. Alles, was Module
  // auflistet, liest direkt aus diesem Array — ein nicht lizenziertes Modul
  // existiert dadurch für den Mandanten schlicht nicht.
  useEffect(() => {
    if (applyLicensedModules(readLicensedModules(entitlement?.features ?? null))) {
      setPlatformMetaVersion(v => v + 1);
    }
  }, [entitlement]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(PLATFORM_MODULES_URL);
        if (!res.ok) return;
        const meta = readPlatformMetaResponse(await res.json() as unknown);
        if (cancelled || !meta) return;
        applyPlatformModuleMeta(meta);
        setPlatformMetaVersion(v => v + 1);
      } catch {
        // Ohne Overrides bleibt der Stand aus dem Code gültig.
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setEntitlement(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      const remote = await fetchEntitlementFromApi(tenantId);
      const base: TenantEntitlement = remote ?? createTrialEntitlement(tenantId);
      if (cancelled) return;
      setEntitlement(base);
      setIsLoading(false);

      const accountTenantId = (account?.tenantId ?? "").toLowerCase();
      const sameTenant = !!accountTenantId && accountTenantId === tenantId.toLowerCase();

      // If the persisted name is a placeholder and we can talk to Graph,
      // try to fetch the real organization name from Microsoft 365.
      if (account && sameTenant && !base.accountDeactivated && isPlaceholderName(base.name)) {
        const graphName = await fetchOrgDisplayNameFromGraph(instance, account);
        if (!cancelled && graphName && graphName !== base.name) {
          const updated: TenantEntitlement = { ...base, name: graphName };
          setEntitlement(updated);
          saveEntitlementToApi(updated);
        }
      }

      // Personen ohne explizite Zuweisung verwenden den Mandanten-Standard
      // direkt in Dashboard, Lern- und Testbereich. Er wird absichtlich nicht
      // als Einzelzuweisung gespeichert: Ändert der Admin den Standard, gilt
      // er dadurch sofort für alle weiterhin unzugewiesenen Personen.
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, instance, account]);

  const hasFeature = (feature: keyof TenantFeatures): boolean => {
    const currentEntitlement = entitlement;
    if (!canUseEntitlement(currentEntitlement)) return false;
    const value = currentEntitlement.features[feature];
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    return false;
  };

  const isFeatureLimited = (feature: "training_modules_max" | "seats_max", count: number): boolean => {
    const currentEntitlement = entitlement;
    if (!canUseEntitlement(currentEntitlement)) return true;
    const max = currentEntitlement.features[feature];
    if (max === -1) return false;
    return count >= max;
  };

  const saveEntitlement = async (e: TenantEntitlement): Promise<boolean> => {
    const normalized = normalizeEntitlement(e, e.tenantId);
    if (!normalized) return false;
    setEntitlement(normalized);
    return saveEntitlementToApi(normalized);
  };

  // Gezielter Server-Merge nur der Logo-Keys: Ein Voll-PUT scheitert am
  // Features-Safety-Guard, sobald der Client-Stand veraltet ist. State wird
  // erst NACH erfolgreichem Write aktualisiert (kein optimistisches Update).
  const patchCobrand = async (patch: CobrandPatch): Promise<boolean> => {
    if (!tenantId) return false;
    try {
      const res = await authFetch("/api/entitlements", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, cobrandPatch: patch }),
      });
      if (!res.ok) return false;
      setEntitlement(prev => {
        if (!prev) return prev;
        const features = { ...prev.features };
        if (patch.logo === null) delete features.cobrand_logo;
        else if (typeof patch.logo === "string") features.cobrand_logo = patch.logo;
        if (patch.logoDark === null) delete features.cobrand_logo_dark;
        else if (typeof patch.logoDark === "string") features.cobrand_logo_dark = patch.logoDark;
        if (typeof patch.name === "string" && patch.name.trim()) features.cobrand_name = patch.name.trim();
        if (!features.cobrand_logo && !features.cobrand_logo_dark) delete features.cobrand_name;
        return { ...prev, features };
      });
      return true;
    } catch {
      return false;
    }
  };

  const applyFeatures = useCallback((patch: Partial<TenantFeatures>) => {
    setEntitlement(prev => (prev ? { ...prev, features: { ...prev.features, ...patch } } : prev));
  }, []);

  // authGetSwr meldet sich zweimal (Cache, dann Server) – übernommen wird nur die
  // vom Server bestätigte Antwort, sonst springt die Seite kurz auf den alten Stand.
  const refreshCancelRef = useRef<(() => void) | null>(null);
  const refreshEntitlement = useCallback(() => {
    if (!tenantId) return;
    refreshCancelRef.current?.();
    refreshCancelRef.current = authGetSwr(`/api/entitlements?tenantId=${encodeURIComponent(tenantId)}`, (data, fresh) => {
      if (!fresh) return;
      const next = normalizeEntitlement(data, tenantId);
      // Ein unveränderter Stand behält seine Referenz. Modul-Pläne setzt bei jeder neuen
      // Referenz alle Eingaben zurück – ein Auffrischen im Hintergrund, das nichts
      // Neues bringt, darf das nicht auslösen.
      if (next) setEntitlement(prev => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    });
  }, [tenantId]);
  useEffect(() => () => refreshCancelRef.current?.(), []);

  const isLocked = isTenantLocked(entitlement);
  const expiryBusinessDays = entitlement ? businessDaysUntil(entitlement.validUntil) : null;

  return (
    <EntitlementContext.Provider value={{ entitlement, hasFeature, isFeatureLimited, saveEntitlement, patchCobrand, applyFeatures, refreshEntitlement, isLoading, isLocked, expiryBusinessDays, moduleMetaVersion: platformMetaVersion }}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlement(): EntitlementContextValue {
  return useContext(EntitlementContext);
}

/**
 * Hält den Mandanten-Stand (Themen, Farben, Module) in einer offenen Sitzung aktuell.
 * Der Start kommt bis zu zwei Stunden aus dem lokalen Cache und lädt nur für den nächsten
 * Besuch nach; ohne das sähe eine Person eine Änderung, die eine andere Person oder ein
 * anderer Tab gerade gespeichert hat, erst beim übernächsten Öffnen. Deshalb frisch holen:
 * beim Öffnen, bei jedem Seitenwechsel (`routeKey`) und wenn man zum Tab zurückkehrt.
 * Nur für Ansichten ohne ungespeicherte Eingaben gedacht (Mitarbeiter-Bereich) – der
 * Admin-Bereich holt seinen Stand dort, wo er bearbeitet wird.
 */
export function useEntitlementRevalidation(routeKey?: string) {
  const { refreshEntitlement } = useEntitlement();
  const lastRef = useRef(0);
  const revalidate = useCallback((minGapMs: number) => {
    if (Date.now() - lastRef.current < minGapMs) return;
    lastRef.current = Date.now();
    refreshEntitlement();
  }, [refreshEntitlement]);

  useEffect(() => { revalidate(15_000); }, [routeKey, revalidate]);

  useEffect(() => {
    const onReturn = () => { if (document.visibilityState === "visible") revalidate(30_000); };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [revalidate]);
}
